import { isValid, parse } from 'date-fns';
import { z } from 'zod';

import { Gender } from '~entities/student/modal/student';
import { validateDob } from '~features/import-wizard/dob-age';
import { validatePhone } from '~features/import-wizard/phone-validate';
import { REQUIRED_PARENT_HEADERS } from './types';

/** Suffix for "Student N Date of Birth" columns. */
const DOB_KEY_SUFFIX = 'Date of Birth';
/** Suffix for any "... Primary Phone" column (parent, student, emergency). */
const PHONE_KEY_SUFFIX = 'Primary Phone';

const isValidDateFormat = (value?: string) => {
  if (!value) return true;
  try {
    // Try MM-dd-yyyy format
    let parsedDate = parse(value, 'MM-dd-yyyy', new Date());
    if (isValid(parsedDate)) {
      return true;
    }

    // Try MM/dd/YYYY format
    parsedDate = parse(value, 'MM/dd/yyyy', new Date());
    return isValid(parsedDate);
  } catch (error) {
    console.error('🚀 ~ isValidDateFormat ~ error:', error);
    return false;
  }
};

export interface ParentRegistrationData {
  'Parent First Name': string; // required
  'Parent Last Name': string; // required
  'Parent Email': string; // required
  'Parent Primary Phone': string; // required
  Relation?: 'Father' | 'Mother' | 'Guardian';

  'Address Apartment'?: string;
  'Address City'?: string;
  'Address Country'?: string;
  'Address State'?: string;
  'Address Street'?: string;
  'Address Zip'?: string;

  'Emergency Contact 1 Authorize Pickup'?: 'Yes' | 'No';
  'Emergency Contact 1 Email'?: string;
  'Emergency Contact 1 First Name'?: string;
  'Emergency Contact 1 Last Name'?: string;
  'Emergency Contact 1 Primary Phone'?: string;

  'Student 1 First Name': string;
  'Student 1 Gender': Gender;
  'Student 1 Last Name': string;
  'Student 1 Date of Birth': string;
  'Student 1 Allergies'?: string;
  'Student 1 Costume Size'?: string;
  'Student 1 Disabilities'?: string;
  'Student 1 Email'?: string;
  'Student 1 Health Insurance Carrier'?: string;
  'Student 1 Medications'?: string;
  'Student 1 Primary Doctor'?: string;
  'Student 1 Primary Phone'?: string;
  'Student 1 Special Needs'?: string;
  'Student 1 Transportation'?: string;

  'Student 2 First Name'?: string;
  'Student 2 Gender'?: Gender;
  'Student 2 Last Name'?: string;
  'Student 2 Date of Birth'?: string;
  'Student 2 Allergies'?: string;
  'Student 2 Costume Size'?: string;
  'Student 2 Disabilities'?: string;
  'Student 2 Email'?: string;
  'Student 2 Health Insurance Carrier'?: string;
  'Student 2 Medications'?: string;
  'Student 2 Primary Doctor'?: string;
  'Student 2 Primary Phone'?: string;
  'Student 2 Special Needs'?: string;
  'Student 2 Transportation'?: string;

  'Student 3 First Name'?: string;
  'Student 3 Gender'?: Gender;
  'Student 3 Last Name'?: string;
  'Student 3 Date of Birth'?: string;
  'Student 3 Allergies'?: string;
  'Student 3 Costume Size'?: string;
  'Student 3 Disabilities'?: string;
  'Student 3 Email'?: string;
  'Student 3 Health Insurance Carrier'?: string;
  'Student 3 Medications'?: string;
  'Student 3 Primary Doctor'?: string;
  'Student 3 Primary Phone'?: string;
  'Student 3 Special Needs'?: string;
  'Student 3 Transportation'?: string;

  'Student 4 First Name'?: string;
  'Student 4 Gender'?: Gender;
  'Student 4 Last Name'?: string;
  'Student 4 Date of Birth'?: string;
  'Student 4 Allergies'?: string;
  'Student 4 Costume Size'?: string;
  'Student 4 Disabilities'?: string;
  'Student 4 Email'?: string;
  'Student 4 Health Insurance Carrier'?: string;
  'Student 4 Medications'?: string;
  'Student 4 Primary Doctor'?: string;
  'Student 4 Primary Phone'?: string;
  'Student 4 Special Needs'?: string;
  'Student 4 Transportation'?: string;

  'Student 5 First Name'?: string;
  'Student 5 Gender'?: Gender;
  'Student 5 Last Name'?: string;
  'Student 5 Date of Birth'?: string;
  'Student 5 Allergies'?: string;
  'Student 5 Costume Size'?: string;
  'Student 5 Disabilities'?: string;
  'Student 5 Email'?: string;
  'Student 5 Health Insurance Carrier'?: string;
  'Student 5 Medications'?: string;
  'Student 5 Primary Doctor'?: string;
  'Student 5 Primary Phone'?: string;
  'Student 5 Special Needs'?: string;
  'Student 5 Transportation'?: string;
}

export const parentSchema = z.object({
  // Parent Information
  'Parent Email': z
    .string()
    .transform((val) => val.trim()) // Remove any whitespace
    .pipe(
      z
        .string()
        .min(1, { message: 'Email is required' })
        .email({
          message: 'Invalid email format. Example: name@example.com',
        }),
    ),
  'Parent First Name': z
    .string()
    .nonempty({ message: 'First name is required' }),
  'Parent Last Name': z.string().nonempty({ message: 'Last name is required' }),
  'Parent Primary Phone': z
    .string()
    .max(15, { message: 'Phone number must not exceed 15 characters' }),
  Relation: z
    .union([
      z.enum(['Father', 'Mother', 'Guardian']),
      z.literal(''),
    ])
    .optional()
    .transform((val) => (val === '' ? undefined : val)),

  // Address Information
  'Address Apartment': z.string().optional(),
  'Address City': z.string().optional(),
  'Address Country': z.string().optional(),
  'Address State': z.string().optional(),
  'Address Street': z.string().optional(),
  'Address Zip': z.string().optional(),

  // Emergency Contact Information
  'Emergency Contact 1 Authorize Pickup': z.enum(['Yes', 'No', '']).optional(),
  'Emergency Contact 1 Email': z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(z.string().email({ message: 'Invalid email address' }).optional()),
  'Emergency Contact 1 First Name': z.string().optional(),
  'Emergency Contact 1 Last Name': z.string().optional(),
  'Emergency Contact 1 Primary Phone': z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(
      z
        .string()
        .max(15, { message: 'Phone number must not exceed 15 characters' })
        .optional(),
    ),

  // Student 1 (required)
  'Student 1 First Name': z
    .string()
    // .nonempty({ message: 'First name is required' })
    .optional(),
  'Student 1 Gender': z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(
      z
        .nativeEnum(Gender, {
          message: `Gender must be one of ${Object.values(Gender).join(', ')}`,
        })
        .optional(),
    ),
  'Student 1 Last Name': z
    .string()
    // .nonempty({ message: 'Last name is required' })
    .optional(),
  'Student 1 Date of Birth': z
    .string()
    .refine(isValidDateFormat, {
      message: 'Invalid date format, expected MM-DD-YYYY',
    })
    .optional(),
  'Student 1 Email': z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(z.string().email({ message: 'Invalid email address' }).optional()),
  'Student 1 Primary Phone': z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(
      z
        .string()
        .max(15, { message: 'Phone number must not exceed 15 characters' })
        .optional(),
    ),
  'Student 1 Allergies': z.string().optional(),
  'Student 1 Costume Size': z.string().optional(),
  'Student 1 Disabilities': z.string().optional(),
  'Student 1 Health Insurance Carrier': z.string().optional(),
  'Student 1 Medications': z.string().optional(),
  'Student 1 Primary Doctor': z.string().optional(),
  'Student 1 Special Needs': z.string().optional(),
  'Student 1 Transportation': z.string().optional(),

  // Student 2 (all optional)
  'Student 2 First Name': z.string().optional(),
  'Student 2 Gender': z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(
      z
        .nativeEnum(Gender, {
          message: `Gender must be one of ${Object.values(Gender).join(', ')}`,
        })
        .optional(),
    ),
  'Student 2 Last Name': z.string().optional(),
  'Student 2 Date of Birth': z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(z.string().refine(isValidDateFormat).optional()),
  'Student 2 Email': z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(z.string().email({ message: 'Invalid email address' }).optional()),
  'Student 2 Primary Phone': z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(
      z
        .string()
        .max(15, { message: 'Phone number must not exceed 15 characters' })
        .optional(),
    ),
  'Student 2 Allergies': z.string().optional(),
  'Student 2 Costume Size': z.string().optional(),
  'Student 2 Disabilities': z.string().optional(),
  'Student 2 Health Insurance Carrier': z.string().optional(),
  'Student 2 Medications': z.string().optional(),
  'Student 2 Primary Doctor': z.string().optional(),
  'Student 2 Special Needs': z.string().optional(),
  'Student 2 Transportation': z.string().optional(),

  // Student 3 (all optional)
  'Student 3 First Name': z.string().optional(),
  'Student 3 Gender': z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(
      z
        .nativeEnum(Gender, {
          message: `Gender must be one of ${Object.values(Gender).join(', ')}`,
        })
        .optional(),
    ),
  'Student 3 Last Name': z.string().optional(),
  'Student 3 Date of Birth': z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(z.string().refine(isValidDateFormat).optional()),
  'Student 3 Email': z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(z.string().email({ message: 'Invalid email address' }).optional()),
  'Student 3 Primary Phone': z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(
      z
        .string()
        .max(15, { message: 'Phone number must not exceed 15 characters' })
        .optional(),
    ),
  'Student 3 Allergies': z.string().optional(),
  'Student 3 Costume Size': z.string().optional(),
  'Student 3 Disabilities': z.string().optional(),
  'Student 3 Health Insurance Carrier': z.string().optional(),
  'Student 3 Medications': z.string().optional(),
  'Student 3 Primary Doctor': z.string().optional(),
  'Student 3 Special Needs': z.string().optional(),
  'Student 3 Transportation': z.string().optional(),

  // Student 4 (all optional)
  'Student 4 First Name': z.string().optional(),
  'Student 4 Gender': z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(
      z
        .nativeEnum(Gender, {
          message: `Gender must be one of ${Object.values(Gender).join(', ')}`,
        })
        .optional(),
    ),
  'Student 4 Last Name': z.string().optional(),
  'Student 4 Date of Birth': z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(z.string().refine(isValidDateFormat).optional()),
  'Student 4 Email': z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(z.string().email({ message: 'Invalid email address' }).optional()),
  'Student 4 Primary Phone': z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(
      z
        .string()
        .max(15, { message: 'Phone number must not exceed 15 characters' })
        .optional(),
    ),
  'Student 4 Allergies': z.string().optional(),
  'Student 4 Costume Size': z.string().optional(),
  'Student 4 Disabilities': z.string().optional(),
  'Student 4 Health Insurance Carrier': z.string().optional(),
  'Student 4 Medications': z.string().optional(),
  'Student 4 Primary Doctor': z.string().optional(),
  'Student 4 Special Needs': z.string().optional(),
  'Student 4 Transportation': z.string().optional(),

  // Student 5 (all optional)
  'Student 5 First Name': z.string().optional(),
  'Student 5 Gender': z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(
      z
        .nativeEnum(Gender, {
          message: `Gender must be one of ${Object.values(Gender).join(', ')}`,
        })
        .optional(),
    ),
  'Student 5 Last Name': z.string().optional(),
  'Student 5 Date of Birth': z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(z.string().refine(isValidDateFormat).optional()),
  'Student 5 Email': z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(z.string().email({ message: 'Invalid email address' }).optional()),
  'Student 5 Primary Phone': z
    .string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(
      z
        .string()
        .max(15, { message: 'Phone number must not exceed 15 characters' })
        .optional(),
    ),
  'Student 5 Allergies': z.string().optional(),
  'Student 5 Costume Size': z.string().optional(),
  'Student 5 Disabilities': z.string().optional(),
  'Student 5 Health Insurance Carrier': z.string().optional(),
  'Student 5 Medications': z.string().optional(),
  'Student 5 Primary Doctor': z.string().optional(),
  'Student 5 Special Needs': z.string().optional(),
  'Student 5 Transportation': z.string().optional(),
});

function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (value === '') return true;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return true;
    // "None" is often used in CSV to mean "not provided"
    if (trimmed.toLowerCase() === 'none') return true;
  }
  return false;
}

export interface RowValidationIssue {
  /** 1-based row position within the uploaded CSV. */
  rowNumber: number;
  column: string;
  value: string;
  errorMessage: string;
}

/**
 * Collects every required-field validation failure across the uploaded rows.
 * Mirrors the Upload button guard (only {@link REQUIRED_PARENT_HEADERS} block
 * upload) so the pre-upload summary and the button stay in sync.
 */
export function collectRequiredFieldIssues(
  csvData: Array<ParentRegistrationData>,
): Array<RowValidationIssue> {
  const issues: Array<RowValidationIssue> = [];
  csvData.forEach((row, index) => {
    (REQUIRED_PARENT_HEADERS as ReadonlyArray<string>).forEach((key) => {
      const raw = row[key as keyof ParentRegistrationData];
      const validation = validateCell(
        key as keyof ParentRegistrationData,
        raw?.replace('None', ''),
      );
      if (!validation.isValid) {
        issues.push({
          rowNumber: index + 1,
          column: key,
          value: raw ?? '',
          errorMessage: validation.errorMessage ?? 'Invalid value',
        });
      }
    });
  });
  return issues;
}

export function validateCell(
  key: keyof ParentRegistrationData,
  value: unknown,
): { isValid: boolean; errorMessage?: string } {
  const isRequired = (
    REQUIRED_PARENT_HEADERS as ReadonlyArray<string>
  ).includes(key as string);

  // Non-required fields: empty/undefined/whitespace is valid (value not provided)
  if (!isRequired && isEmptyValue(value)) {
    return { isValid: true };
  }

  // Required field left empty (e.g. Parent Primary Phone) — flag clearly.
  if (isRequired && isEmptyValue(value)) {
    return { isValid: false, errorMessage: `${String(key)} is required.` };
  }

  // Date-of-birth columns: validate format AND guard against future/unrealistic
  // ages (prevents negative or absurd student ages downstream).
  if (String(key).endsWith(DOB_KEY_SUFFIX)) {
    const { isValid: dobValid, errorMessage } = validateDob(
      typeof value === 'string' ? value : undefined,
    );
    return { isValid: dobValid, errorMessage };
  }

  // Phone columns: format validation without country-code inference.
  if (String(key).endsWith(PHONE_KEY_SUFFIX)) {
    const { isValid: phoneValid, errorMessage } = validatePhone(
      typeof value === 'string' ? value : undefined,
    );
    return { isValid: phoneValid, errorMessage };
  }

  const schema = parentSchema.shape[key];
  // Unmapped / leftover CSV headers are not Enrollio fields — skip, don't crash.
  if (!schema) {
    return { isValid: true };
  }
  const result = schema.safeParse(value);

  if (result.success) {
    return { isValid: true };
  }
  return {
    isValid: false,
    errorMessage: result.error.errors[0]?.message ?? 'Unknown error',
  };
}
